import { useEffect } from 'react'
import { analytics } from '@omnea-app/fe-analytics/client'
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { FeatureFlagsContextValue } from './types'

/**
 * Feature flags to track as user-level properties in Mixpanel.
 * If we were on the higher Mixpanel plan, we could track this at the group/organisation level,
 * but we're not, so we track it at the user level.
 */
const FEATURE_FLAGS = [
  FeatureFlagsEnum.FORM_LEVEL_AI_DOC_EXTRACTION,
  FeatureFlagsEnum.ENABLE_DOCUMENT_EXTRACTION,
  FeatureFlagsEnum.AI_FORM_SUMMARISATION,
  FeatureFlagsEnum.FORM_INSIGHTS_AI,
  FeatureFlagsEnum.AI_CHAT_ASSISTANT,
  FeatureFlagsEnum.AI_CONFIG_BUILDER_CHAT,
  FeatureFlagsEnum.AI_REC_ENGINE_ALTERNATIVES,
  FeatureFlagsEnum.AI_TONE_GENERATION,
  FeatureFlagsEnum.AI_FORM_GENERATION,
  FeatureFlagsEnum.AI_REQUEST_NAME_GENERATION,
  FeatureFlagsEnum.TPRM,
  FeatureFlagsEnum.ENABLE_ASSESSMENTS,
  FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING,
  FeatureFlagsEnum.CREATE_AGREEMENT,
  FeatureFlagsEnum.AGREEMENT_ROLLED_OUT,
  FeatureFlagsEnum.PRICE_BENCHMARKING,
  FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS,
] as const

/**
 * Enriches Mixpanel user properties with feature flag statuses.
 * This merges with existing user properties set in Auth.tsx.
 *
 * Called from FeatureFlagsProvider to ensure feature flags are available.
 */
export const useEnrichUserAnalyticsWithFlags = (userId: string, featureFlags: FeatureFlagsContextValue) => {
  useEffect(() => {
    const featureFlagsToTrack = FEATURE_FLAGS.reduce(
      (acc, flag) => ({
        ...acc,
        [flag]: featureFlags[flag] ?? false,
      }),
      {} as Record<string, boolean>
    )

    void analytics.then((analyticsClient) => analyticsClient.identify(userId, { featureFlags: featureFlagsToTrack }))
  }, [userId, featureFlags])
}
